import { Component, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { Client } from 'src/providers/client/client';
import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from './../../shared/providers/tidy-storage';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Loading } from 'src/shared/components/loading/loading';
import { TimeoutableComponent } from 'src/shared/components/timeoutable/timeoutable.component';
import { Util } from 'src/shared/util/util';
import { CustomerGuestsApiService } from 'src/shared/services/customer-guests-api.service';
import type { GuestPickerValue } from 'src/shared/components/guest-picker/guest-picker.component';

import { dollarsToCents } from 'src/shared/util/money';
import { validateMinimumPrice } from 'src/shared/validator/validateMinimumPrice';

import { CreateGuestReservation, MINIMUM_RESERVATION_PRICE_CENTS } from 'src/models/guest-reservations';
import { GuestReservationResponse } from 'src/models/schedule-weekly-view.model';

import { DateTime as LuxonDateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';

@Component({
    templateUrl: 'add-reservation.html',
    standalone: false
})
export class AddReservationPage extends TimeoutableComponent implements OnInit {
  address: any;
  guestReservation: GuestReservationResponse;
  detail: any;
  reservationDetails: any;
  hasReservationDetails = false;
  dialogParams: any;
  isRightSideContent = true;
  form: UntypedFormGroup;
  timesItems = this.getAllTimeItems();
  addressResponse: any;
  addressFilter: any;
  submitted = false;
  // The guest a refused try already created: the next try reuses it rather than
  // saving the same guest again.
  private createdGuest: { payload: string; id: number } | null = null;

  boundAddReservation = this.addReservation.bind(this);

constructor(
    private guestReservations: GuestReservations,
    private storage: TidyStorage,
    private utils: Util,
    private rightSidePanelService: RightSidePanelService,
    private client: Client,
    private navCtrl: CustomNavController,
    private guestsApi: CustomerGuestsApiService
  ) {
    super();
    this.form = new UntypedFormGroup({
      property: new UntypedFormControl(null, Validators.required),
      guest: new UntypedFormControl(null, Validators.required),
      checkInDate: new UntypedFormControl(null, Validators.required),
      checkInTime: new UntypedFormControl(null),
      checkOutDate: new UntypedFormControl(null, Validators.required),
      checkOutTime: new UntypedFormControl(null),
      price: new UntypedFormControl(null, validateMinimumPrice(MINIMUM_RESERVATION_PRICE_CENTS)),
      guests: new UntypedFormControl(null),
      adults: new UntypedFormControl(null),
      children: new UntypedFormControl(null),
      pets: new UntypedFormControl(null),
      infants: new UntypedFormControl(null),
    });
  }

  onGuestChange(value: GuestPickerValue | null): void {
    this.form.get('guest').setValue(value);
  }

  async ngOnInit() {
    this.isRightSideContent = (await this.storage.retrieve('dialog-right-side-open')) || false;
    this.dialogParams = await this.storage.retrieve('dialog-params');
    this.loadDataAndParams();
  }

  @Loading('', true)
  async loadDataAndParams() {
    await this.getAddressData();
    this.loaded = true;
  }

  async getAddressData() {
    this.addressFilter = await this.buildAddressFilter();
    const storedAddress = await this.storage.retrieve('address');
    if (storedAddress) {
      this.form.patchValue({
        property: storedAddress,
      });
    }
  }

  async buildAddressFilter() {
    this.addressResponse = await this.client.getAddresses();
    return this.client.parseAddressList(this.addressResponse, false);
  }

  getAllTimeItems() {
    return Array.from({ length: 48 }, (_, index) => {
      const luxonDateTimeObject = LuxonDateTime
        .now()
        .startOf('day')
        .plus({ minutes: index * 30 });
      const hour12Format = luxonDateTimeObject.toFormat('h:mma').toLowerCase();
      const militaryHourFormat = luxonDateTimeObject.toFormat('HH:mm');
      return { viewValue: hour12Format, value: militaryHourFormat };
    });
  }

  async addReservation() {
    this.submitted = true;
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    const loading = await this.utils.showLoading();
    try {
      const guestId = await this.resolveGuestId();
      const priceInCents = dollarsToCents(this.form.value.price);
      const payload: CreateGuestReservation = {
        address_id: this.form.value.property,
        check_in_date: this.form.value.checkInDate,
        check_in_time: this.form.value.checkInTime,
        check_out_date: this.form.value.checkOutDate,
        check_out_time: this.form.value.checkOutTime,
        total_guests: this.form.value.guests,
        total_adults: this.form.value.adults,
        total_children: this.form.value.children,
        total_pets: this.form.value.pets,
        total_infants: this.form.value.infants,
        ...(priceInCents === null ? {} : { amount: priceInCents }),
        ...(guestId ? { guests: [{ guest_id: guestId, is_booker: true, is_staying: true }] } : {}),
      };
      await this.guestReservations.create(payload);
      loading.dismiss();
      this.utils.showSuccess('Reservation created successfully');
      if (this.isRightSideContent) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateBack('more/reservations')
      }
    } catch (err) {
      this.submitted = false;
      loading.dismiss();
      const errorMessage = err?.error?.message
        || err?.message
        || 'Error creating reservation, please try again.';
      this.utils.showError(errorMessage);
    }
  }

  // Resolves the guest to attach: an existing guest id, or a freshly created guest.
  private async resolveGuestId(): Promise<number | undefined> {
    const guest = this.form.value.guest as GuestPickerValue | null;
    if (guest?.mode === 'existing') {
      return guest.guest.id;
    }
    if (guest?.mode === 'new') {
      const payload = JSON.stringify(guest.payload);
      if (this.createdGuest?.payload !== payload) {
        const created = await firstValueFrom(this.guestsApi.createGuest(guest.payload));
        this.createdGuest = { payload, id: created.id };
      }
      return this.createdGuest.id;
    }
    return undefined;
  }

}
